// Post-feedback questions for Reasoning Gap sessions. Identical in both conditions.
//
// FEEDBACK_RATING is asked right after each attempt's hints are shown, before the student
// chooses to revise or finish, so every attempt's hints get their own rating.
// AI_USE_SURVEY is the end survey (why and how students used AI tools on the quiz).
// Answers are stored verbatim keyed by `id`; renaming an id splits the dataset.

const AGREE_5 = ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree']

export const FEEDBACK_RATING = [
  { id: 'hints_accurate', type: 'scale', label: 'The hints pointed to real gaps in my solution or my explanation.', options: AGREE_5 },
  { id: 'hints_helpful', type: 'scale', label: 'The hints helped me understand what I had missed.', options: AGREE_5 },
  { id: 'hints_clear', type: 'scale', label: 'The hints were clear and easy to act on.', options: AGREE_5 },
  { id: 'gap_in_own_words', type: 'text', label: 'In your own words, what gap (if any) did the hints point you to?' },
  { id: 'would_change', type: 'text', label: 'Based on these hints, what would you change in your solution (if anything)?', optional: true },
]

// The one option that names the quiz's concepts. Answers are stored as the option text, so the
// CART wording must stay exactly as it was; other quizzes send their own (task.surveyConceptOption).
const CART_CONCEPT_OPTION = 'Recalling how CART / SSE works'

export const AI_USE_SURVEY = [
  {
    id: 'used_ai',
    type: 'radio',
    label: 'Did you use any AI while working on this quiz (the built-in AI assistant, or outside tools such as ChatGPT, Claude, Copilot or Gemini)?',
    options: ['No', 'Yes, a little', 'Yes, substantially'],
  },
  {
    id: 'ai_tools',
    type: 'multi',
    label: 'Which tools did you use?',
    options: ['The built-in AI assistant', 'ChatGPT', 'Claude', 'GitHub Copilot', 'Gemini', 'Other'],
    showIf: a => a.used_ai && a.used_ai !== 'No',
  },
  {
    id: 'ai_purposes',
    type: 'multi',
    label: 'What did you use AI for? (select all that apply)',
    options: [
      'Understanding the problem statement',
      CART_CONCEPT_OPTION,
      'Planning an approach',
      'Writing some or all of the code',
      'Debugging an error',
      'Checking my answer or edge cases',
      'Writing or improving my explanation',
      'Other',
    ],
    showIf: a => a.used_ai && a.used_ai !== 'No',
  },
  {
    id: 'ai_why',
    type: 'text',
    label: 'Why did you decide to use AI at the point(s) you did?',
    showIf: a => a.used_ai && a.used_ai !== 'No',
  },
  {
    id: 'ai_why_not',
    type: 'text',
    label: 'Why did you decide not to use AI?',
    showIf: a => a.used_ai === 'No',
  },
  {
    id: 'confidence_before_feedback',
    type: 'scale',
    label: 'Before seeing the hints, how confident were you that your solution was correct?',
    options: ['Not at all', 'Slightly', 'Moderately', 'Very', 'Completely'],
  },
  {
    id: 'difficulty',
    type: 'scale',
    label: 'How difficult was this quiz?',
    options: ['Very easy', 'Easy', 'Moderate', 'Hard', 'Very hard'],
  },
  { id: 'comments', type: 'text', label: 'Anything else you would like to share?', optional: true },
]

export function isAnswered (q, answers) {
  const v = answers[q.id]
  if (q.type === 'multi') return Array.isArray(v) && v.length > 0
  return typeof v === 'string' ? v.trim().length > 0 : v != null
}

export function visibleQuestions (questions, answers) {
  return questions.filter(q => !q.showIf || q.showIf(answers))
}

export function isComplete (questions, answers) {
  return visibleQuestions(questions, answers).every(q => q.optional || isAnswered(q, answers))
}

// Drops answers to questions hidden by showIf, so a changed branch doesn't leave stale data.
export function visibleAnswers (questions, answers) {
  const out = {}
  for (const q of visibleQuestions(questions, answers)) if (answers[q.id] !== undefined) out[q.id] = answers[q.id]
  return out
}

// The end survey for a quiz whose concept option differs from CART's.
export function aiUseSurveyFor (conceptOption) {
  if (!conceptOption || conceptOption === CART_CONCEPT_OPTION) return AI_USE_SURVEY
  return AI_USE_SURVEY.map(q => (q.id === 'ai_purposes'
    ? { ...q, options: q.options.map(o => (o === CART_CONCEPT_OPTION ? conceptOption : o)) }
    : q))
}
